// Navigation search predicates — pure and testable.
//
// The sidebar search must keep the MODULE PATH visible: results are shown inside
// their group / sub-group hierarchy (e.g. Settings → Utility → Sample Sites) so a
// user can tell apart the SAME form name that exists under different modules
// (user request 2026-09-27). AppShell renders the tree filtered to the branches
// these predicates flag as hits, keeping the headers on the way down.
//
// Kept out of AppShell.tsx so the branch-matching logic can be unit-tested
// against the real nav tree without rendering the shell or logging in.
import { fuzzyMatch } from '../utils/fuzzyMatch';
import type { NavGroupChild, NavItem } from './navData';

/** (id, label) → whether the current user may see this nav leaf. */
export type CanSee = (id: string, label: string) => boolean;

/** A term matches a label. An empty term matches everything (no filtering). */
export const navMatchesTerm = (term: string, label: string): boolean =>
  !term || fuzzyMatch(term, label);

/** True when a child has at least one leaf the user can see (recurses sub-groups). */
export function navChildHasVisibleLeaves(c: NavGroupChild, canSee: CanSee): boolean {
  if (c.kind === 'subhead') return false;
  if (c.kind === 'item') return canSee(c.item.id, c.item.label);
  return c.subgroup.children.some((cc) => navChildHasVisibleLeaves(cc, canSee));
}

/** Visible AND a search hit somewhere in this child's subtree — an item is a
 *  direct label match; a sub-group matches on its OWN label or any descendant. */
export function navChildHasSearchHit(c: NavGroupChild, term: string, canSee: CanSee): boolean {
  if (c.kind === 'subhead') return false;
  if (c.kind === 'item') return canSee(c.item.id, c.item.label) && navMatchesTerm(term, c.item.label);
  const sg = c.subgroup;
  if (!sg.children.some((cc) => navChildHasVisibleLeaves(cc, canSee))) return false;
  return navMatchesTerm(term, sg.label) || sg.children.some((cc) => navChildHasSearchHit(cc, term, canSee));
}

/** True when a top-level group has a search hit — its own label, a flat item,
 *  or any structured descendant. */
export function navGroupHasSearchHit(
  g: { label: string; items: NavItem[]; children?: NavGroupChild[] },
  term: string,
  canSee: CanSee,
): boolean {
  if (navMatchesTerm(term, g.label)) return true;
  if (g.items.some((it) => canSee(it.id, it.label) && navMatchesTerm(term, it.label))) return true;
  return (g.children ?? []).some((c) => navChildHasSearchHit(c, term, canSee));
}
